/**
 * Turn-based REST client (§ F171).
 *
 * Every call except the registry carries `Authorization: Bearer <token>` — the
 * HMAC player token trivia mints, because this surface reuses the trivia
 * `players` table and there is one family roster. The identity itself is
 * `lib/player.ts`'s, shared with every other surface (§ F117): a match is
 * addressed to a PERSON, so "who am I" has to be the same answer here as it is
 * on the tiles and in the lobby.
 *
 * EVERY MUTATION ANSWERS WITH THE WHOLE MATCH. There is no socket on this
 * surface and there does not need to be one — the other player is, by
 * construction, not here — so the cheapest correct thing is for the server to
 * hand back the new truth rather than have the board guess at it and refetch.
 */

import type { StoredPlayer } from "../lib/player";
import type { TurnGameListing, WireMatch, WireMatchSummary } from "./types";

export class TurnApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = "TurnApiError";
  }
}

async function call<T>(
  path: string,
  opts: { method?: string; token?: string; body?: unknown } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  // § F100's rule, kept: every call gives up after 12s instead of hanging. A
  // board gates its keyboard on "a request is in flight", so a fetch that never
  // settles is a keyboard that never comes back.
  const res = await fetch(path, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: AbortSignal.timeout(12_000),
  });

  type Envelope = { success?: boolean; error?: string; data?: T };
  let payload: Envelope | null = null;
  try {
    payload = (await res.json()) as Envelope;
  } catch {
    payload = null;
  }
  if (!res.ok || !payload?.success) {
    throw new TurnApiError(payload?.error || "Something went wrong", res.status);
  }
  return payload.data as T;
}

export function fetchTurnGames(): Promise<{ games: TurnGameListing[] }> {
  return call("/api/turn-based/games");
}

export function fetchMatches(
  player: StoredPlayer,
  gameId?: string
): Promise<{ matches: WireMatchSummary[] }> {
  const params = new URLSearchParams({ player_id: player.id });
  if (gameId) params.set("game", gameId);
  return call(`/api/turn-based/matches?${params.toString()}`, { token: player.token });
}

export function fetchMatch(player: StoredPlayer, matchId: string): Promise<{ match: WireMatch }> {
  const params = new URLSearchParams({ player_id: player.id });
  return call(`/api/turn-based/matches/${encodeURIComponent(matchId)}?${params.toString()}`, {
    token: player.token,
  });
}

/** Challenge somebody. The word travels WITH the challenge — see the route. */
export function createMatch(
  player: StoredPlayer,
  gameId: string,
  opponentId: string,
  word: string
): Promise<{ match_id: string }> {
  return call("/api/turn-based/matches", {
    method: "POST",
    token: player.token,
    body: { player_id: player.id, game_id: gameId, opponent_id: opponentId, word },
  });
}

/** Set the next word. Refused unless it is this player's turn to. */
export function setWord(
  player: StoredPlayer,
  matchId: string,
  word: string
): Promise<{ match: WireMatch }> {
  return call(`/api/turn-based/matches/${encodeURIComponent(matchId)}/word`, {
    method: "POST",
    token: player.token,
    body: { player_id: player.id, word },
  });
}

/**
 * Take a guess. `accepted: false` means the word is not in the dictionary —
 * NOTHING WAS SPENT and the row shakes (§ F100). It is a 200 rather than a 400
 * on purpose: being told "that is not a word" is a normal part of playing this
 * game, not an error.
 */
export function submitGuess(
  player: StoredPlayer,
  matchId: string,
  guess: string,
  /** § F185 — the keystroke tape since the last Enter that reached the server. */
  keys = "",
  /** § F215 — Doodle: ms since my playback of the drawing began. */
  atMs?: number
): Promise<{ accepted: boolean; match: WireMatch }> {
  return call(`/api/turn-based/matches/${encodeURIComponent(matchId)}/guess`, {
    method: "POST",
    token: player.token,
    body: { player_id: player.id, guess, keys: keys.slice(-400), ...(atMs === undefined ? {} : { at_ms: atMs }) },
  });
}

/**
 * § F187 — flush the keystroke tape typed since the last flush. `keepalive`
 * for the page-is-going-away flush, which must outlive the page.
 */
export async function appendKeys(
  player: StoredPlayer,
  matchId: string,
  keys: string,
  keepalive = false
): Promise<void> {
  const res = await fetch(`/api/turn-based/matches/${encodeURIComponent(matchId)}/keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${player.token}` },
    body: JSON.stringify({ player_id: player.id, keys: keys.slice(-400) }),
    keepalive,
  });
  if (!res.ok) throw new Error(`keys ${res.status}`);
}

/** End the match. Jake's one ending condition. */
export function quitMatch(
  player: StoredPlayer,
  matchId: string
): Promise<{ ended: boolean; match: WireMatch }> {
  return call(`/api/turn-based/matches/${encodeURIComponent(matchId)}/quit`, {
    method: "POST",
    token: player.token,
    body: { player_id: player.id },
  });
}

// ── § F198 — Head To Head Doodle ─────────────────────────────────────────────

export type DoodleDifficulty = "easy" | "medium" | "difficult";
export interface DoodleDeal {
  word: string;
  difficulty: DoodleDifficulty;
  /** The server's signature on this word + difficulty; sent back with the drawing. */
  ticket: string;
}

/** Deal a word of this difficulty. `not` skips words already dealt this sitting. */
export function dealDoodle(
  player: StoredPlayer,
  difficulty: DoodleDifficulty,
  not: string[] = [],
  /** § F200 — the match it is for, so no word of that match comes back. */
  matchId?: string
): Promise<DoodleDeal> {
  return call("/api/turn-based/doodle/deal", {
    method: "POST",
    token: player.token,
    body: { player_id: player.id, difficulty, not, ...(matchId ? { match_id: matchId } : {}) },
  });
}

/** Challenge somebody with a drawing. */
export function createDoodleMatch(
  player: StoredPlayer,
  opponentId: string,
  deal: DoodleDeal,
  drawing: unknown
): Promise<{ match_id: string }> {
  return call("/api/turn-based/matches", {
    method: "POST",
    token: player.token,
    body: { player_id: player.id, game_id: "doodle", opponent_id: opponentId, ...deal, drawing },
  });
}

/** Send the next drawing in a match. */
export function sendDrawing(
  player: StoredPlayer,
  matchId: string,
  deal: DoodleDeal,
  drawing: unknown
): Promise<{ match: WireMatch }> {
  return call(`/api/turn-based/matches/${encodeURIComponent(matchId)}/drawing`, {
    method: "POST",
    token: player.token,
    body: { player_id: player.id, ...deal, drawing },
  });
}

/** Give up on the drawing (or word) in front of me. */
export function giveUpRound(player: StoredPlayer, matchId: string): Promise<{ match: WireMatch }> {
  return call(`/api/turn-based/matches/${encodeURIComponent(matchId)}/give-up`, {
    method: "POST",
    token: player.token,
    body: { player_id: player.id },
  });
}
